import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import MobileCashFab from "@/components/MobileCashFab";
import HomeHeroCard from "@/components/v2/HomeHeroCard";
import { useUiRedesignV2 } from "@/lib/uiRedesignV2";
import { fmtINR, fmtINRCompact, fmtPct, fmtDate, currentMonth } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { usePrefs } from "@/contexts/PrefsContext";
import {
    TrendingUp,
    TrendingDown,
    Wallet,
    Receipt,
    Gift,
    CreditCard,
    Banknote,
    ArrowRight,
    ChevronRight,
    AlertTriangle,
    Sparkles,
    ShieldCheck,
    Landmark,
    LineChart,
    Package,
    Coins,
    Repeat,
    FileText,
    ClipboardCheck,
    BarChart3,
    Clock,
    ArrowUpFromLine,
    ArrowDownToLine,
    Info,
    RefreshCcw,
    EyeOff,
    Settings2,
    RotateCcw,
    Star,
} from "lucide-react";

/* ═════════════════════════════════════════════════════════════════
   HOME v3 — "MY FINANCIAL COMMAND CENTRE"

   Sections (in order):
     1. Executive KPI strip (6 cards)
     2. Financial Position — assets vs liabilities → net worth
     3. This Month's Financial Performance
     4. Credit Position — utilisation bar + drilldowns
     5. What Changed — period-over-period intelligence
     6. Action Centre
     7. Explore VAULT — 6 module hubs

   Every card and figure is clickable and routes to the underlying module.
   No fabricated data — empty states show meaningful CTAs.
   ═════════════════════════════════════════════════════════════════ */

// ─── shared UI atoms ─────────────────────────────────────────────
function Trend({ value, small = false }) {
    if (value === null || value === undefined || Number.isNaN(value)) return null;
    const up = value > 0, down = value < 0;
    const cls = up ? "text-emerald-600" : down ? "text-rose-600" : "text-stone-500";
    const Icon = up ? TrendingUp : down ? TrendingDown : null;
    return (
        <span className={`inline-flex items-center gap-1 ${cls} ${small ? "text-[11px]" : "text-xs"} font-medium`}>
            {Icon && <Icon size={small ? 11 : 13} strokeWidth={2.2} />}
            {fmtPct(value)}
        </span>
    );
}

function Section({ title, subtitle, action, actionTo, children, widgetKey, onHide }) {
    return (
        <section className="mb-8" data-widget={widgetKey}>
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900 tracking-tight dark:text-white">{title}</h2>
                    {subtitle && <p className="text-[13px] text-stone-500 mt-0.5 dark:text-stone-400">{subtitle}</p>}
                </div>
                <div className="flex items-center gap-3">
                    {onHide && widgetKey && (
                        <button
                            onClick={() => onHide(widgetKey)}
                            className="text-[12px] text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 inline-flex items-center gap-1"
                            data-testid={`hide-widget-${widgetKey}`}
                            title="Hide this widget"
                        >
                            <EyeOff size={12} /> Hide
                        </button>
                    )}
                    {action && actionTo && (
                        <Link
                            to={actionTo}
                            className="inline-flex items-center gap-1 text-[13px] font-medium text-stone-600 hover:text-stone-900 dark:text-stone-300 dark:hover:text-white"
                            data-testid={`section-action-${title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {action} <ChevronRight size={14} />
                        </Link>
                    )}
                </div>
            </div>
            {children}
        </section>
    );
}

function KPICard({ title, value, delta, description, to, icon: Icon, subLine, accent = "neutral", testId }) {
    const accentBar = {
        emerald: "bg-emerald-500",
        rose: "bg-rose-500",
        amber: "bg-amber-500",
        blue: "bg-blue-500",
        violet: "bg-violet-500",
        neutral: "bg-stone-400",
    }[accent] || "bg-stone-400";
    return (
        <Link
            to={to}
            data-testid={testId}
            className="group relative bg-white border border-stone-200 rounded-2xl p-5 hover:border-stone-300 hover:shadow-md transition-all overflow-hidden block"
        >
            <span className={`absolute top-0 left-0 w-full h-0.5 ${accentBar}`} />
            <div className="flex items-start justify-between mb-3">
                <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{title}</div>
                <span className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center text-stone-500 group-hover:text-stone-800 transition-colors">
                    <Icon size={15} strokeWidth={1.8} />
                </span>
            </div>
            <div className="text-2xl font-bold text-stone-900 tabular-nums tracking-tight">
                {value}
            </div>
            {description && (
                <div className="text-[11.5px] text-stone-500 mt-1">{description}</div>
            )}
            <div className="mt-3 flex items-center justify-between">
                {delta !== undefined ? <Trend value={delta} /> : <span />}
                {subLine && <span className="text-[11px] text-stone-400">{subLine}</span>}
            </div>
        </Link>
    );
}

function EmptyState({ icon: Icon, title, cta, to }) {
    return (
        <div className="bg-white border border-stone-200 border-dashed rounded-2xl p-8 text-center">
            {Icon && <Icon size={22} strokeWidth={1.4} className="mx-auto text-stone-400 mb-2" />}
            <p className="text-sm text-stone-600">{title}</p>
            {cta && to && (
                <Link
                    to={to}
                    className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-stone-800 hover:text-stone-900"
                >
                    {cta} <ArrowRight size={13} />
                </Link>
            )}
        </div>
    );
}

// ─── Cash Position Today card ──────────────────────────────────────
function CashPositionCard({ cashPos }) {
    const accounts = cashPos?.accounts || [];
    const total = cashPos?.total_balance ?? 0;
    return (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden dark:bg-stone-900 dark:border-stone-800" data-testid="cash-position-card">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                {accounts.map((a) => {
                    const reconciled = a.last_recon_status === "Reconciled";
                    return (
                        <Link
                            key={a.id}
                            to={`/cash-desk?tab=ledger`}
                            className="p-5 border-r border-b md:border-b-0 border-stone-100 last:border-r-0 hover:bg-stone-50 transition-colors dark:border-stone-800 dark:hover:bg-stone-800/50"
                            data-testid={`cash-pos-${a.type.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <span className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                    <Coins size={14} />
                                </span>
                                <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{a.name}</div>
                            </div>
                            <div className="text-2xl font-bold text-stone-900 tabular-nums dark:text-white">{fmtINR(a.current_balance)}</div>
                            <div className="flex items-center justify-between mt-3">
                                <span className="text-[11px] text-stone-500">
                                    {a.last_reconciled ? `Recon: ${a.last_reconciled}` : "Not reconciled"}
                                </span>
                                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                    reconciled
                                        ? "bg-emerald-50 text-emerald-700"
                                        : "bg-amber-50 text-amber-700"
                                }`}>
                                    {reconciled ? "Reconciled" : "Pending"}
                                </span>
                            </div>
                        </Link>
                    );
                })}
            </div>
            <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between bg-stone-50 dark:bg-stone-800/40 dark:border-stone-800">
                <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Total physical cash</div>
                <div className="text-lg font-bold text-stone-900 tabular-nums dark:text-white" data-testid="cash-pos-total">{fmtINR(total)}</div>
            </div>
        </div>
    );
}

// ─── the page ──────────────────────────────────────────────────────
import ForexTicker from "@/components/ForexTicker";
import WowInsightsStrip from "@/components/WowInsightsStrip";
import AttentionTodayStrip from "@/components/AttentionTodayStrip";
import WhatsComingStrip from "@/components/WhatsComingStrip";
export default function Home() {
    const { user } = useAuth();
    const { prefs, updatePrefs, toggleWidget, pinModule } = usePrefs();
    const navigate = useNavigate();
    const { enabled: v2Enabled } = useUiRedesignV2();
    const [nw, setNw] = useState(null);
    const [ds, setDs] = useState(null); // /dashboard/summary
    const [fr, setFr] = useState(null); // /finance-review
    const [dh, setDh] = useState(null); // /data-health/score
    const [inv, setInv] = useState(null);
    const [bills, setBills] = useState(null);
    const [monthlySpend, setMonthlySpend] = useState(null);
    const [cashPos, setCashPos] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(new Date().toISOString());

    async function load() {
        setLoading(true);
        try {
            const [nwR, dsR, frR, dhR, invR, billsR, spendR, cashR] = await Promise.all([
                api.get("/net-worth").catch(() => ({ data: null })),
                api.get("/dashboard/summary").catch(() => ({ data: null })),
                api.get("/finance-review").catch(() => ({ data: null })),
                api.get("/data-health/score").catch(() => ({ data: null })),
                api.get("/investments/summary").catch(() => ({ data: null })),
                api.get("/bills/summary").catch(() => ({ data: null })),
                api.get("/analytics/reports/monthly-spend").catch(() => ({ data: null })),
                api.get("/cash/accounts").catch(() => ({ data: null })),
            ]);
            setNw(nwR.data); setDs(dsR.data); setFr(frR.data); setDh(dhR.data);
            setInv(invR.data); setBills(billsR.data); setMonthlySpend(spendR.data);
            setCashPos(cashR.data);
            setLastRefresh(new Date().toISOString());
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    // ─── Setup Wizard auto-redirect ────────────────────────────────
    // On the first landing after login, if the user has completed fewer
    // than 3 onboarding steps, take them straight to /setup. A
    // sessionStorage flag ensures we only nudge them once per session —
    // no redirect loops if they navigate back to home.
    useEffect(() => {
        if (!user || user.role === "expense_user") return;
        if (sessionStorage.getItem("vault_setup_check_done") === "1") return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/setup/progress");
                sessionStorage.setItem("vault_setup_check_done", "1");
                if (!cancelled && data && (data.done ?? 0) < 3) {
                    navigate("/setup", { replace: true });
                }
            } catch {
                // Silent fail — never block the home page for a check.
                sessionStorage.setItem("vault_setup_check_done", "1");
            }
        })();
        return () => { cancelled = true; };
    }, [user, navigate]);

    // Derived — this month's spend
    const spendThisMonth = useMemo(() => {
        const biz = ds?.business_expenses_month ?? 0;
        const per = ds?.personal_expenses_month ?? 0;
        return biz + per;
    }, [ds]);

    // Derived — last month's spend from the historical series
    const spendLastMonth = useMemo(() => {
        if (!monthlySpend?.months || monthlySpend.months.length < 2) return null;
        const arr = monthlySpend.months;
        const prev = arr[arr.length - 2];
        return (prev?.business ?? 0) + (prev?.personal ?? 0);
    }, [monthlySpend]);

    const monthSpendDeltaPct = useMemo(() => {
        if (spendLastMonth === null || spendLastMonth === 0) return null;
        return ((spendThisMonth - spendLastMonth) / Math.abs(spendLastMonth)) * 100;
    }, [spendThisMonth, spendLastMonth]);

    const upcomingPayments = useMemo(() => {
        const dues = fr?.payments_due || [];
        return dues.reduce((s, p) => s + Number(p.amount ?? p.total_amount_due ?? 0), 0);
    }, [fr]);

    // Build the six KPI cards
    const kpis = [
        {
            title: "Net Worth",
            value: fmtINRCompact(nw?.net_worth),
            description: nw?.as_of ? `Valued ${fmtDate(nw.as_of)}` : "Live",
            delta: nw?.trend?.delta_pct,
            to: "/hub/wealth",
            icon: TrendingUp,
            accent: (nw?.trend?.delta_pct ?? 0) >= 0 ? "emerald" : "rose",
            subLine: nw?.trend?.delta != null ? `${fmtINRCompact(nw.trend.delta)} this week` : null,
            testId: "kpi-net-worth",
        },
        {
            title: "Spent This Month",
            value: fmtINRCompact(spendThisMonth),
            description: `${currentMonth()} · biz + personal`,
            delta: monthSpendDeltaPct,
            to: "/expenses",
            icon: Receipt,
            accent: (monthSpendDeltaPct ?? 0) > 0 ? "amber" : "emerald",
            subLine: spendLastMonth != null ? `Prev: ${fmtINRCompact(spendLastMonth)}` : null,
            testId: "kpi-spent-month",
        },
        {
            title: "Rewards Saved",
            value: fmtINR(ds?.total_rewards_points ? ds.total_rewards_points * 0.25 : 0),
            description: `${(ds?.total_rewards_points ?? 0).toLocaleString("en-IN")} pts (₹0.25/pt est)`,
            to: "/offers",
            icon: Gift,
            accent: "violet",
            subLine: "Est. redemption value",
            testId: "kpi-rewards",
        },
        {
            title: "Available Credit",
            value: fmtINRCompact(ds?.available_limit),
            description: `${(ds?.utilization_pct ?? 0).toFixed(1)}% utilised`,
            to: "/hub/credit",
            icon: Wallet,
            accent: (ds?.utilization_pct ?? 0) > 70 ? "rose" : (ds?.utilization_pct ?? 0) > 30 ? "amber" : "emerald",
            subLine: `of ${fmtINRCompact(ds?.total_credit_limit)}`,
            testId: "kpi-available-credit",
        },
        {
            title: "Card Outstanding",
            value: fmtINRCompact(ds?.total_outstanding),
            description: `${ds?.active_cards ?? 0} active cards`,
            to: "/cards",
            icon: CreditCard,
            accent: "blue",
            subLine: ds?.high_utilization_count > 0 ? `${ds.high_utilization_count} > 70% util` : null,
            testId: "kpi-outstanding",
        },
        {
            title: "Upcoming Payments",
            value: fmtINRCompact(upcomingPayments || ds?.total_due_this_month),
            description: `${fr?.counts?.payments_due ?? ds?.cards_due_next_7 ?? 0} in next 7 days`,
            to: "/finance-review",
            icon: Banknote,
            accent: (fr?.counts?.payments_due ?? 0) > 0 ? "amber" : "emerald",
            subLine: ds?.overdue_cards ? `${ds.overdue_cards} overdue` : null,
            testId: "kpi-upcoming-payments",
        },
    ];

    // "What Changed" narratives — only from real data
    const whatChanged = useMemo(() => {
        const items = [];
        if (nw?.trend?.delta != null) {
            const d = nw.trend.delta;
            if (Math.abs(d) > 1) {
                items.push({
                    icon: d > 0 ? TrendingUp : TrendingDown,
                    tone: d > 0 ? "positive" : "negative",
                    text: `Your net worth ${d > 0 ? "grew" : "dropped"} by ${fmtINR(Math.abs(d))} this week (${fmtPct(nw.trend.delta_pct)}).`,
                    to: "/hub/wealth",
                });
            }
        }
        if (spendThisMonth && spendLastMonth != null && Math.abs(spendThisMonth - spendLastMonth) > 100) {
            const diff = spendThisMonth - spendLastMonth;
            items.push({
                icon: diff > 0 ? TrendingUp : TrendingDown,
                tone: diff > 0 ? "negative" : "positive",
                text: `Spending is ${diff > 0 ? "up" : "down"} ${fmtINR(Math.abs(diff))} vs last month.`,
                to: "/expenses",
            });
        }
        if ((ds?.utilization_pct ?? 0) > 30) {
            items.push({
                icon: AlertTriangle,
                tone: (ds?.utilization_pct ?? 0) > 70 ? "negative" : "warning",
                text: `Credit utilisation is at ${(ds.utilization_pct).toFixed(1)}%. ${
                    ds.utilization_pct > 70 ? "Above 70% — pay down or ask for a limit hike." : "Aim to stay under 30% for the best score impact."
                }`,
                to: "/hub/credit",
            });
        }
        if ((ds?.high_utilization_count ?? 0) > 0) {
            items.push({
                icon: AlertTriangle,
                tone: "warning",
                text: `${ds.high_utilization_count} card(s) are above 70% utilisation.`,
                to: "/cards",
            });
        }
        if ((fr?.counts?.overdue_cards ?? 0) > 0) {
            items.push({
                icon: AlertTriangle,
                tone: "negative",
                text: `${fr.counts.overdue_cards} card payment(s) overdue.`,
                to: "/payments",
            });
        }
        if ((fr?.counts?.missing_statements ?? 0) > 0) {
            items.push({
                icon: FileText,
                tone: "warning",
                text: `${fr.counts.missing_statements} statement(s) still missing for closed cycles.`,
                to: "/statements",
            });
        }
        if (items.length === 0) {
            items.push({
                icon: ShieldCheck,
                tone: "positive",
                text: "Your financial position is stable this period. No unusual movement detected.",
                to: "/finance-review",
            });
        }
        return items.slice(0, 6);
    }, [nw, spendThisMonth, spendLastMonth, ds, fr]);

    // Action Centre — pull from finance-review + alerts
    const actions = useMemo(() => {
        const list = [];
        (fr?.payments_due || []).slice(0, 3).forEach((p) => list.push({
            key: `pay-${p.card_id}`, priority: "high", type: "Payment due",
            icon: Banknote, title: `${p.bank_name || "Card"} · ${p.last_4 ? `•••• ${p.last_4}` : ""}`,
            due: p.due_date, amount: p.amount ?? p.total_amount_due, to: "/payments",
        }));
        (fr?.overdue_cards || []).slice(0, 3).forEach((p) => list.push({
            key: `overdue-${p.card_id}`, priority: "critical", type: "Overdue",
            icon: AlertTriangle, title: `${p.bank_name || "Card"} · overdue`,
            due: p.due_date, amount: p.amount ?? p.total_amount_due, to: "/payments",
        }));
        (fr?.missing_statements || []).slice(0, 3).forEach((s) => list.push({
            key: `stmt-${s.card_id}-${s.month}`, priority: "med", type: "Statement missing",
            icon: FileText, title: `${s.bank_name || "Card"} · ${s.month}`,
            to: "/statements",
        }));
        if ((fr?.counts?.payments_not_reflected ?? 0) > 0) list.push({
            key: "not-reflected", priority: "med", type: "To reconcile",
            icon: RefreshCcw, title: `${fr.counts.payments_not_reflected} payment(s) not yet reflected`,
            to: "/reconciliation",
        });
        if ((fr?.counts?.unapproved_expenses ?? 0) > 0) list.push({
            key: "unapproved", priority: "low", type: "Approvals",
            icon: ClipboardCheck, title: `${fr.counts.unapproved_expenses} expense(s) awaiting approval`,
            to: "/expenses",
        });
        return list.slice(0, 8);
    }, [fr]);

    if (loading && !nw) {
        return <div className="p-8 text-sm text-stone-500" data-testid="home-loading">Loading command centre…</div>;
    }

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="cc-home">
            {/* ═══════ VAULT 2.0 · MIDNIGHT LUXE Home Hero (founder-gated) ═══════ */}
            {v2Enabled && <HomeHeroCard />}

            {/* Header strip */}
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-1">
                        {user?.name?.split(" ")[0] || "Welcome"}
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">
                        My VAULT
                    </h1>
                </div>
                <div className="text-[12px] text-stone-500 flex items-center gap-3 dark:text-stone-400">
                    <span className="inline-flex items-center gap-1.5" data-testid="last-refresh">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Refreshed {fmtDate(lastRefresh)}
                    </span>
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-900 dark:hover:text-white"
                        data-testid="home-refresh-btn"
                    >
                        <RefreshCcw size={12} /> Refresh
                    </button>
                    <button
                        onClick={() => updatePrefs({ compact_view: !prefs.compact_view })}
                        className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-900 dark:hover:text-white"
                        data-testid="toggle-compact-view"
                        title={prefs.compact_view ? "Detailed view" : "Compact view"}
                    >
                        <Settings2 size={12} /> {prefs.compact_view ? "Detailed" : "Compact"}
                    </button>
                    {(prefs.homepage_hidden_widgets || []).length > 0 && (
                        <button
                            onClick={() => updatePrefs({ homepage_hidden_widgets: [] })}
                            className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-900 dark:hover:text-white"
                            data-testid="reset-layout-btn"
                            title="Show all hidden widgets"
                        >
                            <RotateCcw size={12} /> Show all
                        </button>
                    )}
                </div>
            </div>

            {/* Live Forex ticker — subtle marquee above the KPI strip */}
            <ForexTicker />

            {/* ═══════ HERO 1: What Needs Attention Today ═══════ */}
            <AttentionTodayStrip />

            {/* ═══════ HERO 2: WOW Insights — health · leak · split · subs · CA pack ═══════ */}
            <WowInsightsStrip />

            {/* ═══════ What's Coming — compact 30-day preview from VAULT Calendar ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("whats-coming") && <WhatsComingStrip />}

            {/* ═══════ Action Centre — hoisted to first-screen so users see
                    "what needs action today" before any charts. ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("actions") && (
            <Section title="Action Centre" subtitle="Prioritised — attend the top items first." action="See all" actionTo="/finance-review" widgetKey="actions" onHide={toggleWidget}>
                {actions.length === 0 ? (
                    <EmptyState icon={ShieldCheck} title="Nothing needs your attention. Nicely done." />
                ) : (
                    <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
                        {actions.map((a) => (
                            <Link
                                key={a.key}
                                to={a.to}
                                className="flex items-center gap-4 px-4 py-3 hover:bg-stone-50 transition-colors"
                                data-testid={`action-${a.key}`}
                            >
                                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                    a.priority === "critical" ? "bg-rose-50 text-rose-600" :
                                    a.priority === "high" ? "bg-amber-50 text-amber-600" :
                                    a.priority === "med" ? "bg-blue-50 text-blue-600" : "bg-stone-100 text-stone-500"
                                }`}>
                                    <a.icon size={14} strokeWidth={1.8} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{a.type}</span>
                                        {a.due && <span className="text-[11px] text-stone-500">· due {fmtDate(a.due)}</span>}
                                    </div>
                                    <div className="text-sm text-stone-800 truncate">{a.title}</div>
                                </div>
                                {a.amount != null && (
                                    <span className="text-sm font-semibold text-stone-900 tabular-nums shrink-0">
                                        {fmtINR(a.amount)}
                                    </span>
                                )}
                                <ChevronRight size={15} className="text-stone-400 shrink-0" />
                            </Link>
                        ))}
                    </div>
                )}
            </Section>
            )}

            {/* ═══════ 1. Executive KPI strip ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("kpi") && (
            <Section title="At a glance" subtitle="Six numbers that answer the essentials." widgetKey="kpi" onHide={toggleWidget}>
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${prefs.compact_view ? "xl:grid-cols-6" : "xl:grid-cols-6"} gap-3`}>
                    {kpis.map((k) => <KPICard key={k.title} {...k} />)}
                </div>
            </Section>
            )}

            {/* ═══════ Cash Position Today ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("cash-position") && cashPos?.accounts?.length > 0 && (
            <Section
                title="Cash Position Today"
                subtitle="Physical cash across your Petty · Service · Personal accounts."
                action="Open Cash Desk"
                actionTo="/cash-desk"
                widgetKey="cash-position"
                onHide={toggleWidget}
            >
                <CashPositionCard cashPos={cashPos} />
            </Section>
            )}

            {/* ═══════ 2. Financial Position ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("position") && (
            <Section
                title="Financial Position — As of Today"
                subtitle="Everything you own minus everything you owe."
                action="Full breakdown"
                actionTo="/hub/wealth"
                widgetKey="position"
                onHide={toggleWidget}
            >
                <FinancialPositionCard nw={nw} inv={inv} bills={bills} />
            </Section>
            )}

            {/* ═══════ 3. This Month's Performance ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("performance") && (
            <Section
                title="This Month's Financial Performance"
                subtitle="Period-based metrics for the current month."
                action="Open reports"
                actionTo="/reports"
                widgetKey="performance"
                onHide={toggleWidget}
            >
                <MonthlyPerformanceCard ds={ds} bills={bills} monthlySpend={monthlySpend} />
            </Section>
            )}

            {/* ═══════ 4. Credit Position ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("credit") && (
            <Section
                title="Credit Position"
                subtitle="How much you've borrowed vs what's still available."
                action="Manage cards"
                actionTo="/cards"
                widgetKey="credit"
                onHide={toggleWidget}
            >
                <CreditPositionCard ds={ds} fr={fr} />
            </Section>
            )}

            {/* ═══════ 5. What Changed ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("changed") && (
            <Section title="What Changed" subtitle="Real deltas — never fabricated." widgetKey="changed" onHide={toggleWidget}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {whatChanged.map((wc, i) => (
                        <Link
                            key={i}
                            to={wc.to}
                            className="bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 transition-colors flex items-start gap-3"
                            data-testid={`what-changed-${i}`}
                        >
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                wc.tone === "positive" ? "bg-emerald-50 text-emerald-600" :
                                wc.tone === "negative" ? "bg-rose-50 text-rose-600" :
                                wc.tone === "warning" ? "bg-amber-50 text-amber-600" : "bg-stone-100 text-stone-500"
                            }`}>
                                <wc.icon size={15} strokeWidth={1.8} />
                            </span>
                            <span className="text-[13px] text-stone-700 leading-relaxed">{wc.text}</span>
                        </Link>
                    ))}
                </div>
            </Section>
            )}

            {/* ═══════ 6. Action Centre — (originally rendered here; hoisted
                     to the top of the page above the KPI strip to answer
                     "what needs action today" before showing charts). ═══════ */}
            {false && !prefs.homepage_hidden_widgets?.includes("actions") && (
            <Section title="Action Centre" subtitle="Prioritised — attend the top items first." action="See all" actionTo="/finance-review" widgetKey="actions" onHide={toggleWidget}>
                {actions.length === 0 ? (
                    <EmptyState icon={ShieldCheck} title="Nothing needs your attention. Nicely done." />
                ) : (
                    <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
                        {actions.map((a) => (
                            <Link
                                key={a.key}
                                to={a.to}
                                className="flex items-center gap-4 px-4 py-3 hover:bg-stone-50 transition-colors"
                                data-testid={`action-${a.key}`}
                            >
                                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                    a.priority === "critical" ? "bg-rose-50 text-rose-600" :
                                    a.priority === "high" ? "bg-amber-50 text-amber-600" :
                                    a.priority === "med" ? "bg-blue-50 text-blue-600" : "bg-stone-100 text-stone-500"
                                }`}>
                                    <a.icon size={14} strokeWidth={1.8} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{a.type}</span>
                                        {a.due && <span className="text-[11px] text-stone-500">· due {fmtDate(a.due)}</span>}
                                    </div>
                                    <div className="text-sm text-stone-800 truncate">{a.title}</div>
                                </div>
                                {a.amount != null && (
                                    <span className="text-sm font-semibold text-stone-900 tabular-nums shrink-0">
                                        {fmtINR(a.amount)}
                                    </span>
                                )}
                                <ChevronRight size={15} className="text-stone-400 shrink-0" />
                            </Link>
                        ))}
                    </div>
                )}
            </Section>
            )}

            {/* ═══════ 7. Explore VAULT — module cards ═══════ */}
            {!prefs.homepage_hidden_widgets?.includes("modules") && (
            <Section title="Explore VAULT" subtitle="Six pillars — cards-first navigation. Pin favourites with the star." widgetKey="modules" onHide={toggleWidget}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {(() => {
                        const cards = [
                            { title: "Money", description: "Expenses · Income · Bank Accounts · Budgets · Payments", to: "/hub/money", icon: Wallet, accent: "emerald", metric: ds ? `${fmtINRCompact(spendThisMonth)} spent` : null, slug: "money" },
                            { title: "Credit", description: "Credit Cards · Statements · Limits · Payments · Rewards", to: "/hub/credit", icon: CreditCard, accent: "blue", metric: ds ? `${ds.active_cards ?? 0} active cards` : null, slug: "credit" },
                            { title: "Wealth", description: "Investments · Assets · Net Worth · Gold · Property", to: "/hub/wealth", icon: TrendingUp, accent: "violet", metric: nw ? `${fmtINRCompact(nw.net_worth)} net worth` : null, slug: "wealth" },
                            { title: "Commitments", description: "Loans · Subscriptions · Insurance · Recurring Payments", to: "/hub/commitments", icon: Repeat, accent: "amber", metric: bills ? `${fmtINRCompact(bills.monthly_outflow_estimate)}/mo est` : null, slug: "commitments" },
                            { title: "Protection", description: "Warranties · Documents · Policies · Renewals", to: "/hub/protection", icon: ShieldCheck, accent: "rose", slug: "protection" },
                            { title: "Insights", description: "Reports · Tax Records · Reconciliation · Audit Logs", to: "/hub/insights", icon: BarChart3, accent: "neutral", metric: dh ? `Data health ${dh.score}/100` : null, slug: "insights" },
                        ];
                        const pinned = new Set(prefs.pinned_modules || []);
                        const sorted = [...cards].sort((a, b) => (pinned.has(b.slug) ? 1 : 0) - (pinned.has(a.slug) ? 1 : 0));
                        return sorted.map((m) => <ModuleCard key={m.title} {...m} pinned={pinned.has(m.slug)} onPinToggle={() => pinModule(m.slug)} />);
                    })()}
                </div>
            </Section>
            )}

            {/* Data quality strip */}
            {dh && (
                <div className="mt-6 bg-stone-50 border border-stone-200 rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid="data-quality-strip">
                    <span className="inline-flex items-center gap-2 text-[13px] text-stone-700">
                        <Info size={14} className="text-stone-500" />
                        Data health: <strong className="text-stone-900">{dh.score}/100</strong> · <span className="text-stone-500">{dh.label}</span>
                    </span>
                    {Array.isArray(dh.top_gaps) && dh.top_gaps.length > 0 && (
                        <span className="text-[12px] text-stone-500">
                            {dh.top_gaps.length} gap(s) to review
                        </span>
                    )}
                    <Link
                        to="/data-health"
                        className="ml-auto text-[13px] font-medium text-stone-800 hover:text-stone-900 inline-flex items-center gap-1"
                        data-testid="open-data-health"
                    >
                        Open Data Health <ArrowRight size={13} />
                    </Link>
                </div>
            )}
            {/* Mobile-only floating Quick-Add pill — cash + card expense + income */}
            <MobileCashFab variant="all" />
        </div>
    );
}

// ─── Financial Position sub-card ────────────────────────────────
function FinancialPositionCard({ nw, inv, bills }) {
    if (!nw) return <EmptyState icon={Landmark} title="Net worth data unavailable." cta="Set up wealth" to="/hub/wealth" />;
    const assets = [
        { label: "Bank balances", value: nw.assets?.bank_balances, to: "/bank-accounts", icon: Landmark },
        { label: "Investments", value: nw.assets?.investments_current, to: "/investments", icon: LineChart },
        { label: "Assets registry", value: nw.assets?.assets_registry, to: "/assets", icon: Package },
    ];
    const liabilities = [
        { label: "Credit card outstanding", value: nw.liabilities?.credit_cards, to: "/cards", icon: CreditCard },
        { label: "Recurring bills", value: nw.liabilities?.recurring_bills, to: "/bills", icon: Repeat },
    ];
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Assets */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Assets</span>
                    <ArrowUpFromLine size={13} className="text-emerald-500" />
                </div>
                <div className="text-2xl font-bold text-stone-900 tabular-nums mb-3">{fmtINR(nw.total_assets)}</div>
                <div className="space-y-1.5">
                    {assets.map((a) => (
                        <Link key={a.label} to={a.to} className="flex items-center justify-between py-1.5 text-[13px] hover:bg-stone-50 rounded px-2 -mx-2" data-testid={`asset-row-${a.label.replace(/\s+/g, "-").toLowerCase()}`}>
                            <span className="text-stone-600 flex items-center gap-2"><a.icon size={12} strokeWidth={1.6} className="text-stone-400" />{a.label}</span>
                            <span className="tabular-nums font-medium text-stone-900">{fmtINR(a.value)}</span>
                        </Link>
                    ))}
                </div>
            </div>
            {/* Liabilities */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Liabilities</span>
                    <ArrowDownToLine size={13} className="text-rose-500" />
                </div>
                <div className="text-2xl font-bold text-stone-900 tabular-nums mb-3">{fmtINR(nw.total_liabilities)}</div>
                <div className="space-y-1.5">
                    {liabilities.map((l) => (
                        <Link key={l.label} to={l.to} className="flex items-center justify-between py-1.5 text-[13px] hover:bg-stone-50 rounded px-2 -mx-2" data-testid={`liab-row-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>
                            <span className="text-stone-600 flex items-center gap-2"><l.icon size={12} strokeWidth={1.6} className="text-stone-400" />{l.label}</span>
                            <span className="tabular-nums font-medium text-stone-900">{fmtINR(l.value)}</span>
                        </Link>
                    ))}
                </div>
            </div>
            {/* Net worth */}
            <div className="bg-stone-900 text-white rounded-2xl p-5 flex flex-col justify-between">
                <div>
                    <span className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold">Net Worth</span>
                    <div className="text-3xl font-bold tabular-nums mt-2">{fmtINR(nw.net_worth)}</div>
                    {nw.trend?.delta != null && (
                        <div className="mt-2 text-[12px] text-stone-300 flex items-center gap-2">
                            <span className={nw.trend.delta >= 0 ? "text-emerald-400" : "text-rose-400"}>
                                {nw.trend.delta >= 0 ? "▲" : "▼"} {fmtINR(Math.abs(nw.trend.delta))}
                            </span>
                            <span>({fmtPct(nw.trend.delta_pct)}) vs last week</span>
                        </div>
                    )}
                </div>
                <div className="mt-4 text-[11px] text-stone-500 flex items-center gap-1">
                    <Clock size={11} /> Valued {fmtDate(nw.as_of)}
                </div>
            </div>
        </div>
    );
}

// ─── Monthly performance sub-card ───────────────────────────────
function MonthlyPerformanceCard({ ds, bills, monthlySpend }) {
    const items = [
        { label: "Spending", value: (ds?.business_expenses_month ?? 0) + (ds?.personal_expenses_month ?? 0), to: "/expenses" },
        { label: "Business spend", value: ds?.business_expenses_month ?? 0, to: "/expenses?type=business" },
        { label: "Personal spend", value: ds?.personal_expenses_month ?? 0, to: "/expenses?type=personal" },
        { label: "Card payments", value: ds?.total_paid_this_month ?? 0, to: "/payments" },
        { label: "Subscription outflow", value: bills?.monthly_outflow_estimate ?? 0, to: "/bills" },
        { label: "Rewards earned", value: (ds?.total_rewards_points ?? 0) * 0.25, to: "/offers", note: `${(ds?.total_rewards_points ?? 0).toLocaleString("en-IN")} pts` },
    ];
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {items.map((it) => (
                    <Link key={it.label} to={it.to} className="block group" data-testid={`perf-${it.label.replace(/\s+/g, "-").toLowerCase()}`}>
                        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{it.label}</div>
                        <div className="text-xl font-bold text-stone-900 tabular-nums mt-1 group-hover:underline decoration-stone-300 underline-offset-2">
                            {fmtINR(it.value)}
                        </div>
                        {it.note && <div className="text-[11px] text-stone-400 mt-0.5">{it.note}</div>}
                    </Link>
                ))}
            </div>
            <div className="mt-4 pt-4 border-t border-stone-100 text-[12px] text-stone-500">
                Transfers between own accounts and card bill payments are excluded from spend totals. Reversed transactions are removed. Refunds reduce the underlying category.
            </div>
        </div>
    );
}

// ─── Credit position sub-card ───────────────────────────────────
function CreditPositionCard({ ds, fr }) {
    if (!ds) return <EmptyState icon={CreditCard} title="Credit data unavailable." cta="View cards" to="/cards" />;
    const util = Math.max(0, Math.min(100, ds.utilization_pct ?? 0));
    const utilTone = util > 70 ? "bg-rose-500" : util > 30 ? "bg-amber-500" : "bg-emerald-500";
    const items = [
        { label: "Sanctioned limit", value: fmtINR(ds.total_credit_limit), to: "/cards" },
        { label: "Outstanding", value: fmtINR(ds.total_outstanding), to: "/cards" },
        { label: "Available", value: fmtINR(ds.available_limit), to: "/cards" },
        { label: "Active cards", value: (ds.active_cards ?? 0).toString(), to: "/cards" },
        { label: "Due this month", value: fmtINR(ds.total_due_this_month), to: "/payments" },
        { label: "Overdue", value: fmtINR(ds.overdue_amount), to: "/payments" },
    ];
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
            {/* Utilization bar */}
            <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Utilisation</span>
                    <span className="text-sm font-semibold tabular-nums">{util.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-3 overflow-hidden" data-testid="credit-util-bar">
                    <div className={`h-3 ${utilTone} transition-all duration-500`} style={{ width: `${util}%` }} />
                </div>
                <div className="flex justify-between text-[11px] text-stone-500 mt-2">
                    <span>Used: {fmtINRCompact(ds.total_outstanding)}</span>
                    <span>Available: {fmtINRCompact(ds.available_limit)}</span>
                    <span>Limit: {fmtINRCompact(ds.total_credit_limit)}</span>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {items.map((it) => (
                    <Link key={it.label} to={it.to} className="block group" data-testid={`credit-${it.label.replace(/\s+/g, "-").toLowerCase()}`}>
                        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{it.label}</div>
                        <div className="text-lg font-bold text-stone-900 tabular-nums mt-1 group-hover:underline decoration-stone-300">
                            {it.value}
                        </div>
                    </Link>
                ))}
            </div>
            {(ds.high_utilization_count > 0 || (fr?.counts?.missing_statements ?? 0) > 0) && (
                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center gap-4 flex-wrap text-[13px]">
                    {ds.high_utilization_count > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-rose-600">
                            <AlertTriangle size={13} /> {ds.high_utilization_count} card(s) &gt; 70% util
                        </span>
                    )}
                    {(fr?.counts?.missing_statements ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-amber-600">
                            <FileText size={13} /> {fr.counts.missing_statements} statement(s) missing
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Module card (Explore VAULT grid) ───────────────────────────
function ModuleCard({ title, description, to, icon: Icon, accent = "neutral", metric, pinned, onPinToggle }) {
    const accentColor = {
        emerald: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950",
        blue: "text-blue-600 bg-blue-50 dark:bg-blue-950",
        violet: "text-violet-600 bg-violet-50 dark:bg-violet-950",
        amber: "text-amber-600 bg-amber-50 dark:bg-amber-950",
        rose: "text-rose-600 bg-rose-50 dark:bg-rose-950",
        neutral: "text-stone-600 bg-stone-100 dark:bg-stone-800",
    }[accent];
    return (
        <div className={`relative bg-white border rounded-2xl p-5 hover:border-stone-300 hover:shadow-md transition-all group dark:bg-stone-900 dark:border-stone-800 ${pinned ? "ring-1 ring-amber-300 dark:ring-amber-500/50" : "border-stone-200"}`}
            data-testid={`module-${title.toLowerCase()}`}>
            <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPinToggle?.(); }}
                className={`absolute top-3 right-3 w-7 h-7 inline-flex items-center justify-center rounded-full transition-colors ${
                    pinned ? "text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950" : "text-stone-300 hover:text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800"
                }`}
                data-testid={`module-pin-${title.toLowerCase()}`}
                aria-label={pinned ? "Unpin" : "Pin"}
                title={pinned ? "Unpin" : "Pin"}
            >
                <Star size={14} fill={pinned ? "currentColor" : "none"} />
            </button>
            <Link to={to} className="block">
                <div className="flex items-start justify-between mb-3">
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${accentColor}`}>
                        <Icon size={18} strokeWidth={1.7} />
                    </span>
                    <ArrowRight size={15} className="text-stone-300 group-hover:text-stone-700 group-hover:translate-x-0.5 transition-all mr-8" />
                </div>
                <div className="text-lg font-bold text-stone-900 tracking-tight dark:text-white">{title}</div>
                <div className="text-[13px] text-stone-500 mt-1 leading-snug dark:text-stone-400">{description}</div>
                {metric && (
                    <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800 text-[12px] font-medium text-stone-700 tabular-nums dark:text-stone-200">
                        {metric}
                    </div>
                )}
            </Link>
        </div>
    );
}
